import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import API_URLS from '../../config/api';
import axiosInstance from '../../utils/axios';
import { formatDate } from '../../utils/dateUtils';
import { 
  CalendarIcon, 
  MapPinIcon, 
  ClockIcon, 
  UserGroupIcon, 
  PencilIcon, 
  TrashIcon,
  XMarkIcon,
  CheckIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const RideManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [rides, setRides] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [churchDetails, setChurchDetails] = useState(null);
  const [formData, setFormData] = useState({
    date: '',
    time: '',
    pickupStreet: '',
    pickupCity: '',
    pickupPostcode: '',
    dropoffLocation: "Winners Chapel Int'l, Toronto - 1093 Meyerside Dr, Mississauga, ON L5T 1J6",
    availableSeats: '1',
    vehicleId: '',
    serviceSession: '1st',
    rideType: 'return'
  });
  const [editingRide, setEditingRide] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (location.state && location.state.openCreateRide) {
      setShowAddForm(true);
      // Remove the state so it doesn't trigger again
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const resetFormData = () => {
    setFormData({
      date: '',
      time: '',
      pickupStreet: '',
      pickupCity: '',
      pickupPostcode: '',
      dropoffLocation: "Winners Chapel Int'l, Toronto - 1093 Meyerside Dr, Mississauga, ON L5T 1J6",
      availableSeats: '1',
      vehicleId: vehicles.length > 0 ? (vehicles.find(v => v.isDefault)?.id || vehicles[0].id) : '',
      serviceSession: '1st',
      rideType: 'return'
    });
  };

  const handleCloseAddForm = () => {
    setShowAddForm(false);
    resetFormData();
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [ridesResponse, vehiclesResponse] = await Promise.all([
        axiosInstance.get(API_URLS.rides.driver),
        axiosInstance.get(API_URLS.vehicles)
      ]);
      
      setRides(ridesResponse.data);
      setVehicles(vehiclesResponse.data);
      
      // If we have a default vehicle, set it in the form
      const defaultVehicle = vehiclesResponse.data.find(v => v.isDefault);
      if (defaultVehicle) {
        setFormData(prev => ({
          ...prev,
          vehicleId: defaultVehicle.id
        }));
      } else if (vehiclesResponse.data.length > 0) {
        setFormData(prev => ({
          ...prev,
          vehicleId: vehiclesResponse.data[0].id
        }));
      }
      
      // Then try to get church details
      try {
        const churchRes = await axiosInstance.get(API_URLS.church);
        setChurchDetails(churchRes.data);
      } catch (churchError) {
        console.error('Failed to fetch church details:', churchError);
        // Set default church details if API fails
        setChurchDetails({
          name: "Winners Chapel Int'l, Toronto",
          address: "1093 Meyerside Dr, Mississauga, ON L5T 1J6",
          services: [
            { id: "1st", time: "9:00 AM" },
            { id: "2nd", time: "11:00 AM" },
            { id: "3rd", time: "6:00 PM" }
          ]
        });
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Combine address fields and ensure church address is always used
      const submissionData = {
        ...formData,
        pickupLocation: `${formData.pickupStreet}, ${formData.pickupCity} ${formData.pickupPostcode}`,
        dropoffLocation: "Winners Chapel Int'l, Toronto - 1093 Meyerside Dr, Mississauga, ON L5T 1J6"
      };
      
      await axiosInstance.post(API_URLS.rides.create, submissionData);
      setShowAddForm(false);
      resetFormData();
      fetchData();
    } catch (error) {
      console.error('Error creating ride:', error);
      setError('Failed to create ride');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (ride) => {
    setEditingRide(ride);
    
    // Parse pickup location into separate fields
    let pickupStreet = '', pickupCity = '', pickupPostcode = '';
    if (ride.pickupLocation) {
      const parts = ride.pickupLocation.split(', ');
      if (parts.length >= 1) pickupStreet = parts[0] || '';
      if (parts.length >= 2) {
        // Last part might contain city and postcode
        const lastPart = parts[parts.length - 1];
        const spaceIndex = lastPart.lastIndexOf(' ');
        if (spaceIndex > 0) {
          pickupCity = lastPart.substring(0, spaceIndex);
          pickupPostcode = lastPart.substring(spaceIndex + 1);
        } else {
          pickupCity = lastPart;
        }
      }
    }
    
    // Handle time formatting and round to nearest 15-minute interval
    let formattedTime = '';
    if (ride.time) {
      const [hours, minutes] = ride.time.split(':').map(Number);
      // Round minutes to nearest 15-minute interval
      const roundedMinutes = Math.round(minutes / 15) * 15;
      const adjustedHours = roundedMinutes >= 60 ? hours + 1 : hours;
      const finalMinutes = roundedMinutes >= 60 ? 0 : roundedMinutes;
      
      formattedTime = `${adjustedHours.toString().padStart(2, '0')}:${finalMinutes.toString().padStart(2, '0')}`;
    }
    
    setFormData({
      date: ride.date ? new Date(ride.date).toISOString().split('T')[0] : '',
      time: formattedTime,
      pickupStreet,
      pickupCity,
      pickupPostcode,
      dropoffLocation: "Winners Chapel Int'l, Toronto - 1093 Meyerside Dr, Mississauga, ON L5T 1J6",
      availableSeats: ride.availableSeats?.toString() || '1',
      vehicleId: ride.vehicleId || '',
      serviceSession: ride.serviceSession || '1st',
      rideType: ride.rideType || 'return'
    });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingRide) return;
    
    setSubmitting(true);
    try {
      // Combine address fields and ensure church address is always used
      const updateData = {
        ...formData,
        pickupLocation: `${formData.pickupStreet}, ${formData.pickupCity} ${formData.pickupPostcode}`,
        dropoffLocation: "Winners Chapel Int'l, Toronto - 1093 Meyerside Dr, Mississauga, ON L5T 1J6"
      };
      
      await axiosInstance.put(`${API_URLS.rides.create}/${editingRide.id}`, updateData);
      setEditingRide(null);
      fetchData();
    } catch (error) {
      console.error('Error updating ride:', error);
      setError('Failed to update ride');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelRide = async (rideId) => {
    try {
      await axiosInstance.delete(`${API_URLS.rides.create}/${rideId}`);
      fetchData();
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error('Error cancelling ride:', error);
      setError('Failed to cancel ride');
    }
  };
  
  const getServiceSessionLabel = (session) => {
    switch (session) {
      case '1st': return '1st Service';
      case '2nd': return '2nd Service';
      case '3rd': return '3rd Service';
      case 'midweek': return 'Midweek';
      default: return session;
    }
  };

  // Helper function to extract city and postcode from address
  const getCityAndPostcode = (address) => {
    if (!address) return 'N/A';
    
    // Split address by commas
    const parts = address.split(',').map(part => part.trim());
    
    if (parts.length >= 2) {
      // Get the last part (likely contains city and postcode)
      const lastPart = parts[parts.length - 1];
      // If it's just a postcode pattern, get city from second last part
      const postcodePattern = /^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/i;
      
      if (postcodePattern.test(lastPart.trim()) && parts.length >= 3) {
        // Format: "Street, City, Postcode"
        return `${parts[parts.length - 2]}, ${lastPart}`;
      } else {
        // Assume last part contains "City Postcode"
        return lastPart;
      }
    }
    
    return address; // Fallback to full address
  };

  // Generate time options with 15-minute intervals
  const generateTimeOptions = () => {
    const times = [];
    for (let hour = 6; hour <= 23; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const hourStr = hour.toString().padStart(2, '0');
        const minuteStr = minute.toString().padStart(2, '0');
        const time24 = `${hourStr}:${minuteStr}`;
        
        // Convert to 12-hour format for display
        let hour12 = hour;
        let ampm = 'AM';
        
        if (hour === 0) {
          hour12 = 12;
          ampm = 'AM';
        } else if (hour === 12) {
          hour12 = 12;
          ampm = 'PM';
        } else if (hour > 12) {
          hour12 = hour - 12;
          ampm = 'PM';
        }
        
        const displayTime = `${hour12}:${minuteStr} ${ampm}`;
        
        times.push({ value: time24, label: displayTime });
      }
    }
    return times;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">My Rides</h1>
        <button
          onClick={() => setShowAddForm(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
        >
          Create New Ride
        </button>
      </div>

      {error && (
        <div className="mt-4 bg-red-50 p-4 rounded-md">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Add Ride Form */}
      {showAddForm && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-gray-900">Create New Ride</h2>
              <button 
                onClick={handleCloseAddForm}
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label htmlFor="date" className="block text-sm font-medium text-gray-700">
                    Date
                  </label>
                  <input
                    type="date"
                    id="date"
                    name="date"
                    required
                    value={formData.date}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="time" className="block text-sm font-medium text-gray-700">
                      Time
                    </label>
                    <select
                      id="time"
                      name="time"
                      required
                      value={formData.time}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    >
                      <option value="">Select time</option>
                      {generateTimeOptions().map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label htmlFor="serviceSession" className="block text-sm font-medium text-gray-700">
                      Service
                    </label>
                    <select
                      id="serviceSession"
                      name="serviceSession"
                      required
                      value={formData.serviceSession}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    >
                      <option value="1st">1st Service</option>
                      <option value="2nd">2nd Service</option>
                      <option value="3rd">3rd Service</option>
                      <option value="midweek">Midweek</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="rideType" className="block text-sm font-medium text-gray-700">
                    Ride Type
                  </label>
                  <select
                    id="rideType"
                    name="rideType"
                    required
                    value={formData.rideType}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  >
                    <option value="one-way">One-way</option>
                    <option value="return">Return Trip</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.rideType === 'one-way' 
                      ? 'Single trip to church only' 
                      : 'Round trip - to church and back home'
                    }
                  </p>
                </div>
                
                {/* Pickup Address Section */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Pickup Address</h3>
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="pickupStreet" className="block text-sm font-medium text-gray-700">
                        Street Address
                      </label>
                      <div className="mt-1 relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <MapPinIcon className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="text"
                          id="pickupStreet"
                          name="pickupStreet"
                          required
                          value={formData.pickupStreet}
                          onChange={handleInputChange}
                          className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="123 Main Street"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="pickupCity" className="block text-sm font-medium text-gray-700">
                          City
                        </label>
                        <input
                          type="text"
                          id="pickupCity"
                          name="pickupCity"
                          required
                          value={formData.pickupCity}
                          onChange={handleInputChange}
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="Toronto"
                        />
                      </div>

                      <div>
                        <label htmlFor="pickupPostcode" className="block text-sm font-medium text-gray-700">
                          Postcode
                  </label>
                  <input
                    type="text"
                          id="pickupPostcode"
                          name="pickupPostcode"
                    required
                          value={formData.pickupPostcode}
                    onChange={handleInputChange}
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="M5V 3A8"
                  />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <label htmlFor="dropoffLocation" className="block text-sm font-medium text-gray-700">
                    Dropoff Location
                  </label>
                  <div className="mt-1 relative">
                  <input
                    type="text"
                    id="dropoffLocation"
                    name="dropoffLocation"
                    value="Winners Chapel Int'l, Toronto - 1093 Meyerside Dr, Mississauga, ON L5T 1J6"
                      disabled
                      className="block w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed pointer-events-none"
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">🏛️ Church address (fixed destination)</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="availableSeats" className="block text-sm font-medium text-gray-700">
                      Available Seats
                    </label>
                    <input
                      type="number"
                      id="availableSeats"
                      name="availableSeats"
                      required
                      min="1"
                      max="20"
                      value={formData.availableSeats}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="vehicleId" className="block text-sm font-medium text-gray-700">
                      Vehicle
                    </label>
                    <select
                      id="vehicleId"
                      name="vehicleId"
                      required
                      value={formData.vehicleId}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    >
                      <option value="">Select a vehicle</option>
                      {vehicles.map(vehicle => (
                        <option key={vehicle.id} value={vehicle.id}>
                          {vehicle.make} {vehicle.model} - {vehicle.licensePlate}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCloseAddForm}
                  className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  {submitting ? 'Creating...' : 'Create Ride'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Ride Modal */}
      {editingRide && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-gray-900">Edit Ride</h2>
              <button 
                onClick={() => setEditingRide(null)}
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={handleUpdate}>
              <div className="space-y-4">
                <div>
                  <label htmlFor="date" className="block text-sm font-medium text-gray-700">
                    Date
                  </label>
                  <input
                    type="date"
                    id="date"
                    name="date"
                    required
                    value={formData.date}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="time" className="block text-sm font-medium text-gray-700">
                      Time
                    </label>
                    <select
                      id="time"
                      name="time"
                      required
                      value={formData.time}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    >
                      <option value="">Select time</option>
                      {generateTimeOptions().map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label htmlFor="serviceSession" className="block text-sm font-medium text-gray-700">
                      Service
                    </label>
                    <select
                      id="serviceSession"
                      name="serviceSession"
                      required
                      value={formData.serviceSession}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    >
                      <option value="1st">1st Service</option>
                      <option value="2nd">2nd Service</option>
                      <option value="3rd">3rd Service</option>
                      <option value="midweek">Midweek</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="rideType" className="block text-sm font-medium text-gray-700">
                    Ride Type
                  </label>
                  <select
                    id="rideType"
                    name="rideType"
                    required
                    value={formData.rideType}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  >
                    <option value="one-way">One-way</option>
                    <option value="return">Return Trip</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.rideType === 'one-way' 
                      ? 'Single trip to church only' 
                      : 'Round trip - to church and back home'
                    }
                  </p>
                </div>
                
                {/* Pickup Address Section */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Pickup Address</h3>
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="pickupStreet" className="block text-sm font-medium text-gray-700">
                        Street Address
                      </label>
                      <div className="mt-1 relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <MapPinIcon className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="text"
                          id="pickupStreet"
                          name="pickupStreet"
                          required
                          value={formData.pickupStreet}
                          onChange={handleInputChange}
                          className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="123 Main Street"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="pickupCity" className="block text-sm font-medium text-gray-700">
                          City
                        </label>
                        <input
                          type="text"
                          id="pickupCity"
                          name="pickupCity"
                          required
                          value={formData.pickupCity}
                          onChange={handleInputChange}
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="Toronto"
                        />
                      </div>

                      <div>
                        <label htmlFor="pickupPostcode" className="block text-sm font-medium text-gray-700">
                          Postcode
                  </label>
                  <input
                    type="text"
                          id="pickupPostcode"
                          name="pickupPostcode"
                    required
                          value={formData.pickupPostcode}
                    onChange={handleInputChange}
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="M5V 3A8"
                  />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <label htmlFor="dropoffLocation" className="block text-sm font-medium text-gray-700">
                    Dropoff Location
                  </label>
                  <div className="mt-1 relative">
                  <input
                    type="text"
                    id="dropoffLocation"
                    name="dropoffLocation"
                    value="Winners Chapel Int'l, Toronto - 1093 Meyerside Dr, Mississauga, ON L5T 1J6"
                      disabled
                      className="block w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed pointer-events-none"
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">🏛️ Church address (fixed destination)</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="availableSeats" className="block text-sm font-medium text-gray-700">
                      Available Seats
                    </label>
                    <input
                      type="number"
                      id="availableSeats"
                      name="availableSeats"
                      required
                      min="1"
                      max="20"
                      value={formData.availableSeats}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="vehicleId" className="block text-sm font-medium text-gray-700">
                      Vehicle
                    </label>
                    <select
                      id="vehicleId"
                      name="vehicleId"
                      required
                      value={formData.vehicleId}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    >
                      <option value="">Select a vehicle</option>
                      {vehicles.map(vehicle => (
                        <option key={vehicle.id} value={vehicle.id}>
                          {vehicle.make} {vehicle.model} - {vehicle.licensePlate}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setEditingRide(null)}
                  className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  {submitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 bg-red-100 rounded-full p-2 mr-3">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              </div>
              <h2 className="text-lg font-medium text-gray-900">Cancel Ride</h2>
            </div>
            
            <p className="text-sm text-gray-500 mb-4">
              Are you sure you want to cancel this ride? This action cannot be undone 
              and all bookings associated with this ride will be notified.
            </p>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(null)}
                className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                No, Keep Ride
              </button>
              <button
                type="button"
                onClick={() => handleCancelRide(showDeleteConfirm)}
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Yes, Cancel Ride
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-10">
          <p className="text-gray-500">Loading rides...</p>
        </div>
      ) : rides.length === 0 ? (
        <div className="text-center py-10 bg-white shadow overflow-hidden sm:rounded-lg">
          <p className="text-gray-500 mb-4">You haven't created any rides yet.</p>
          <button
            onClick={() => setShowAddForm(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
          >
            Create Your First Ride
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {rides.map((ride) => (
            <div key={ride.id} className="bg-white shadow overflow-hidden rounded-lg">
              <div className="p-5">
                <div className="flex justify-between items-start">
                  <div className="flex items-center space-x-2">
                    <CalendarIcon className="h-5 w-5 text-gray-400" />
                    <h3 className="text-lg font-medium text-gray-900">
                      {formatDate(ride.date)}
                    </h3>
                  </div>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {ride.serviceSession ? getServiceSessionLabel(ride.serviceSession) : 'Service TBD'}
                  </span>
                </div>
                
                <div className="mt-4 space-y-3">
                  <div className="flex items-center text-sm text-gray-500">
                    <ClockIcon className="h-5 w-5 text-gray-400 mr-2" />
                    {ride.time || 'No time specified'}
                  </div>
                  
                  <div className="flex items-start text-sm text-gray-500">
                    <MapPinIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                    <div>
                      <div className="font-medium text-xs mb-1">Pickup:</div>
                      {getCityAndPostcode(ride.pickupLocation)}
                    </div>
                  </div>
                  
                  <div className="flex items-start text-sm text-gray-500">
                    <MapPinIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                    <div>
                      <div className="font-medium text-xs mb-1">Dropoff:</div>
                      Winners Chapel Int'l, Toronto - 1093 Meyerside Dr, Mississauga, ON L5T 1J6
                    </div>
                  </div>
                  
                  <div className="flex items-center text-sm text-gray-500">
                    <UserGroupIcon className="h-5 w-5 text-gray-400 mr-2" />
                    {ride.availableSeats} seat{ride.availableSeats !== 1 ? 's' : ''} available
                  </div>
                </div>
                
                <div className="mt-5 flex justify-end space-x-2">
                  <button
                    onClick={() => handleEdit(ride)}
                    className="inline-flex items-center p-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    title="Edit ride"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(ride.id)}
                    className="inline-flex items-center p-2 border border-gray-300 rounded-md text-sm font-medium text-red-600 bg-white hover:bg-red-50"
                    title="Cancel ride"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => navigate(`/driver/bookings?rideId=${ride.id}`)}
                    className="inline-flex justify-center w-full py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                  >
                    View Bookings
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RideManagement;
